import { type ConversionFunnelChartProps } from "../../../types/analytics"
import dayjs from "dayjs"

interface FunnelStep {
  label: string
  value: number
  percentage: number
  conversionRate?: number
}

export const ConversionFunnelChart: React.FC<ConversionFunnelChartProps> = ({
  analyticsItems,
  startOfCycle,
  showOverlay = false,
}) => {
  const cycleStartDate = dayjs(startOfCycle)
  const filteredAnalytics = analyticsItems.filter(
    (item) => dayjs(item.date).isAfter(cycleStartDate) || dayjs(item.date).isSame(cycleStartDate)
  )

  const totalAppOpened = filteredAnalytics.reduce((sum, item) => sum + item.app_opened, 0)
  const totalVtoUsers = filteredAnalytics.reduce((sum, item) => sum + item.vto_users, 0)
  const totalAddToCartUsers = filteredAnalytics.reduce(
    (sum, item) => sum + item.add_to_cart_users,
    0
  )

  const step1ToStep2Rate = totalAppOpened > 0 ? (totalVtoUsers / totalAppOpened) * 100 : 0
  const step2ToStep3Rate = totalVtoUsers > 0 ? (totalAddToCartUsers / totalVtoUsers) * 100 : 0

  const funnelData: FunnelStep[] = [
    {
      label: "App Opened",
      value: totalAppOpened,
      percentage: 100,
    },
    {
      label: "Tried VTO",
      value: totalVtoUsers,
      percentage: totalAppOpened > 0 ? (totalVtoUsers / totalAppOpened) * 100 : 0,
      conversionRate: step1ToStep2Rate,
    },
    {
      label: "Added to Cart",
      value: totalAddToCartUsers,
      percentage: totalAppOpened > 0 ? (totalAddToCartUsers / totalAppOpened) * 100 : 0,
      conversionRate: step2ToStep3Rate,
    },
  ]

  if (showOverlay)
    return (
      <div className="p-4 bg-card rounded border min-h-[400px]">
        <h3 className="mb-3 text-lg font-semibold tracking-tight text-foreground">
          Conversion Funnel - Loading...
        </h3>
        <div className="h-[300px] bg-muted rounded" />
      </div>
    )

  return (
    <div className="p-6 bg-card rounded-lg border h-full">
      <div className="flex flex-col gap-6 h-full">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            Track user journey from app opening to purchase conversion
          </h3>
        </div>
        <div className="flex-1 flex flex-col justify-center gap-8">
          {funnelData.map((step, index) => {
            const isLast = index === funnelData.length - 1
            const maxValue = funnelData[0]?.value || 1
            const progressWidth = (step.value / maxValue) * 100

            return (
              <div key={step.label} className="flex flex-col gap-4">
                <div className="text-center">
                  <div className="mb-2 text-base font-semibold tracking-tight text-foreground">
                    {step.label}
                  </div>
                  <div className="text-sm text-muted-foreground mb-4">
                    {index === 0 && "Unique users who opened the VTO app"}
                    {index === 1 && "Unique users who used Virtual Try-On features"}
                    {index === 2 && "Unique users who added items to cart after VTO"}
                  </div>
                </div>

                <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-1000 ease-out"
                    style={{ width: `${progressWidth}%` }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <div className="text-xl font-semibold tracking-tight text-foreground">
                      {step.value}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {step.percentage.toFixed(1)}% of total
                    </div>
                  </div>

                  {step.conversionRate !== undefined && (
                    <div className="text-center flex-1">
                      <div className="text-xl font-semibold tracking-tight text-foreground">
                        {step.value}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {step.conversionRate.toFixed(1)}% conversion
                      </div>
                    </div>
                  )}
                </div>

                {!isLast && (
                  <div className="flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 5V19M12 19L19 12M12 19L5 12"
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
